import React, { useState } from 'react';
import { createCustomer, updateCustomer } from '../../services/api';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { COUNTRIES, PHONE_CODES } from './constants';

export default function CustomerForm({ customer, onSaved, onCancel }) {
    const parsePhone = (phone) => {
        if (!phone) return { code: '+32', number: '' };
        const match = phone.match(/^(\+\d{1,4})\s*(.*)$/);
        return match ? { code: match[1], number: match[2] } : { code: '+32', number: phone };
    };
    const parseCountry = (country) => {
        if (!country) return 'BE';
        const found = COUNTRIES.find(c => c.name === country || c.code === country);
        return found ? found.code : 'BE';
    };

    const initForm = () => {
        if (customer) {
            const parsed = parsePhone(customer.phone);
            return {
                name: customer.name || '', company_name: customer.company_name || '',
                customer_type: customer.customer_type || 'corporate',
                tax_number: customer.tax_number || '', tax_office: customer.tax_office || '',
                email: customer.email || '', phone_code: parsed.code, phone_number: parsed.number,
                contact_person: customer.contact_person || '',
                address: customer.address || '', city: customer.city || '',
                country: parseCountry(customer.country), postal_code: customer.postal_code || '',
                price_group: customer.price_group || '', currency: customer.currency || 'EUR',
                notes: customer.notes || '',
            };
        }
        return {
            name: '', company_name: '', customer_type: 'corporate',
            tax_number: '', tax_office: '', email: '',
            phone_code: '+32', phone_number: '', contact_person: '',
            address: '', city: '', country: 'BE', postal_code: '',
            price_group: '', currency: 'EUR', notes: '',
        };
    };

    const [formData, setFormData] = useState(initForm);
    const [saving, setSaving] = useState(false);
    const set = (key, val) => setFormData(prev => ({ ...prev, [key]: val }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const data = { ...formData };
            data.phone = data.phone_number ? `${data.phone_code} ${data.phone_number}` : null;
            delete data.phone_code; delete data.phone_number;
            const countryObj = COUNTRIES.find(c => c.code === data.country);
            data.country = countryObj ? countryObj.name : data.country || null;
            if (!data.tax_office) data.tax_office = null;
            if (!data.email) data.email = null;
            if (!data.contact_person) data.contact_person = null;
            if (!data.address) data.address = null;
            if (!data.city) data.city = null;
            if (!data.country) data.country = null;
            if (!data.postal_code) data.postal_code = null;
            if (!data.price_group) data.price_group = null;
            if (!data.notes) data.notes = null;
            if (!data.company_name) data.company_name = null;

            if (customer) {
                await updateCustomer(customer.id, data);
                toast.success('Musteri guncellendi');
            } else {
                await createCustomer(data);
                toast.success('Musteri olusturuldu');
            }
            onSaved();
        } catch (error) { toast.error(error.response?.data?.detail || 'Kaydetme hatasi'); }
        finally { setSaving(false); }
    };

    return (
        <div className="space-y-5" data-testid="customer-form-page">
            <div className="flex items-center gap-3">
                <button onClick={onCancel} className="p-2 rounded-xl hover:bg-[#F5F5F7] transition-colors" data-testid="back-to-list-btn">
                    <ArrowLeft size={20} className="text-[#1D1D1F]" />
                </button>
                <div>
                    <h2 className="text-lg font-semibold text-[#1D1D1F]" data-testid="customer-form-title">
                        {customer ? 'Musteriyi Duzenle' : 'Yeni Musteri Ekle'}
                    </h2>
                    <p className="text-xs text-[#86868B]">{customer ? 'Musteri bilgilerini guncelleyin' : 'Yeni bir musteri kaydi olusturun'}</p>
                </div>
            </div>

            <form onSubmit={handleSubmit} data-testid="customer-form">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                    <div className="card-apple p-5 space-y-4">
                        <h3 className="text-sm font-semibold text-[#1D1D1F] pb-2 border-b border-[#F5F5F7]">Temel Bilgiler</h3>
                        <div>
                            <label className="text-xs font-medium text-[#86868B] block mb-1.5">Musteri / Firma Adi *</label>
                            <input type="text" value={formData.name} onChange={e => set('name', e.target.value)} className="input-apple" required data-testid="customer-name-input" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-xs font-medium text-[#86868B] block mb-1.5">Musteri Tipi</label>
                                <select value={formData.customer_type} onChange={e => set('customer_type', e.target.value)} className="input-apple" data-testid="customer-type-select">
                                    <option value="corporate">Kurumsal</option>
                                    <option value="individual">Bireysel</option>
                                </select>
                            </div>
                            <div>
                                <label className="text-xs font-medium text-[#86868B] block mb-1.5">Yetkili Kisi</label>
                                <input type="text" value={formData.contact_person} onChange={e => set('contact_person', e.target.value)} className="input-apple" data-testid="customer-contact-input" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-xs font-medium text-[#86868B] block mb-1.5">KDV Numarasi *</label>
                                <input type="text" value={formData.tax_number} onChange={e => set('tax_number', e.target.value)} className="input-apple" required data-testid="customer-tax-number-input" />
                            </div>
                            <div>
                                <label className="text-xs font-medium text-[#86868B] block mb-1.5">Vergi Dairesi</label>
                                <input type="text" value={formData.tax_office} onChange={e => set('tax_office', e.target.value)} className="input-apple" data-testid="customer-tax-office-input" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-xs font-medium text-[#86868B] block mb-1.5">Email</label>
                                <input type="email" value={formData.email} onChange={e => set('email', e.target.value)} className="input-apple" data-testid="customer-email-input" />
                            </div>
                            <div>
                                <label className="text-xs font-medium text-[#86868B] block mb-1.5">Telefon</label>
                                <div className="flex gap-1.5">
                                    <select value={formData.phone_code} onChange={e => set('phone_code', e.target.value)} className="input-apple w-[90px] flex-shrink-0 text-xs" data-testid="customer-phone-code-select">
                                        {PHONE_CODES.map(pc => <option key={pc.code + pc.name} value={pc.code}>{pc.code}</option>)}
                                    </select>
                                    <input type="tel" value={formData.phone_number} onChange={e => set('phone_number', e.target.value)} className="input-apple flex-1" data-testid="customer-phone-input" />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="space-y-5">
                        <div className="card-apple p-5 space-y-4">
                            <h3 className="text-sm font-semibold text-[#1D1D1F] pb-2 border-b border-[#F5F5F7]">Adres Bilgileri</h3>
                            <div>
                                <label className="text-xs font-medium text-[#86868B] block mb-1.5">Adres</label>
                                <textarea value={formData.address} onChange={e => set('address', e.target.value)} className="input-apple min-h-[80px] resize-none" rows={3} data-testid="customer-address-input" />
                            </div>
                            <div className="grid grid-cols-3 gap-3">
                                <div>
                                    <label className="text-xs font-medium text-[#86868B] block mb-1.5">Sehir</label>
                                    <input type="text" value={formData.city} onChange={e => set('city', e.target.value)} className="input-apple" data-testid="customer-city-input" />
                                </div>
                                <div>
                                    <label className="text-xs font-medium text-[#86868B] block mb-1.5">Ulke</label>
                                    <select value={formData.country} onChange={e => set('country', e.target.value)} className="input-apple" data-testid="customer-country-select">
                                        {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="text-xs font-medium text-[#86868B] block mb-1.5">Posta Kodu</label>
                                    <input type="text" value={formData.postal_code} onChange={e => set('postal_code', e.target.value)} className="input-apple" data-testid="customer-postal-input" />
                                </div>
                            </div>
                        </div>

                        <div className="card-apple p-5 space-y-4">
                            <h3 className="text-sm font-semibold text-[#1D1D1F] pb-2 border-b border-[#F5F5F7]">Ticari Bilgiler</h3>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-xs font-medium text-[#86868B] block mb-1.5">Fiyat Grubu</label>
                                    <select value={formData.price_group} onChange={e => set('price_group', e.target.value)} className="input-apple" data-testid="customer-price-group-select">
                                        <option value="">Secin</option>
                                        <option value="A">Fiyat A</option>
                                        <option value="B">Fiyat B</option>
                                        <option value="C">Fiyat C</option>
                                        <option value="D">Fiyat D</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="text-xs font-medium text-[#86868B] block mb-1.5">Para Birimi</label>
                                    <select value={formData.currency} onChange={e => set('currency', e.target.value)} className="input-apple" data-testid="customer-currency-select">
                                        <option value="EUR">EUR</option>
                                        <option value="USD">USD</option>
                                        <option value="TRY">TRY</option>
                                        <option value="GBP">GBP</option>
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label className="text-xs font-medium text-[#86868B] block mb-1.5">Notlar</label>
                                <textarea value={formData.notes} onChange={e => set('notes', e.target.value)} className="input-apple min-h-[60px] resize-none" rows={2} data-testid="customer-notes-input" />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex items-center justify-end gap-3 mt-5 pt-4 border-t border-[#F5F5F7]">
                    <button type="button" onClick={onCancel} className="btn btn-secondary px-6" data-testid="cancel-customer-btn">Iptal</button>
                    <button type="submit" disabled={saving} className="btn btn-primary px-8" data-testid="save-customer-btn">
                        {saving ? 'Kaydediliyor...' : (customer ? 'Guncelle' : 'Olustur')}
                    </button>
                </div>
            </form>
        </div>
    );
}
